// « Mon profil » (#/profil), opened from the avatar menu of the home. Appetite copycat world (grey / white / blue, system font),
// grouped lists. Two blocks: identity + « Mes informations » (edit in a bottom sheet, photo picked locally as a preview), and
// « Mes repères » (birth date, height). Weight, measurements and progress photos stay in Mon suivi (link, no duplicate).
// Session-local demo state (compte-state.ts); nothing is sent or uploaded. Sheets use the shared top handle (drawer.tsx).
// Also exports the small account UI shared with Paramètres (sheet frame, page header, rows, tab bar without active tab).
import { Button } from "@ubm/ui/components/button";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { BookOpen, CalendarDays, Camera, ChartLine, ChevronLeft, ChevronRight, House, Store } from "lucide-react";
import { Avatar } from "@ubm/ui/components/avatar";
import { StatusBar, type Route } from "./parts";
import { CustomSheet } from "./sheet";
import { account, fullName, useAccount, type Account } from "./compte-state";
import { ShellActions } from "./shell";
import { STATUS_LABEL, isConfigured, useHealth } from "./health-state";

interface PageProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }

/* ---------------- Shared account UI (Profil, Paramètres) ---------------- */

/** Bottom sheet of the account pages: same frame as the home sheets, top handle only (tap / swipe down), backdrop. Escape,
 *  focus trap / return and inert background come from the shared engine (CustomSheet); a layer above it (MediaViewer) keeps
 *  its own Escape through the common stack. */
export function AccountSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return <CustomSheet title={title} onClose={onClose} overlayClassName="cc-home-overlay cc-account-overlay" className="cc-home-sheet cc-account-sheet">
    {children}
  </CustomSheet>;
}

/** Page header: back to the home (the account pages are not tabs), then the title. */
export function AccountHeader({ title, go }: { title: string; go: (r: Route) => void }) {
  return <header className="cc-account-head">
    <button type="button" className="cc-icon-btn" aria-label="Retour à l'accueil" onClick={() => go("accueil")}><ChevronLeft size={18} strokeWidth={2} /></button>
    <h1 className="cc-title">{title}</h1>
    <ShellActions />
  </header>;
}


export function AccountGroup({ title, id, children, note }: { title: string; id: string; children: ReactNode; note?: ReactNode }) {
  return <section className="cc-account-group" aria-labelledby={id}>
    <h2 id={id} className="cc-account-group-title">{title}</h2>
    <div className="cc-card cc-account-list">{children}</div>
    {note && <p className="cc-account-note">{note}</p>}
  </section>;
}

export function AccountRow({ label, value, onClick, end }: { label: string; value?: ReactNode; onClick?: () => void; end?: ReactNode }) {
  const inner = <><span className="cc-account-row-label">{label}</span>{value !== undefined && <span className="cc-account-row-value">{value}</span>}{end ?? (onClick && <ChevronRight size={16} color="#c4c4c9" aria-hidden="true" />)}</>;
  return onClick ? <button type="button" className="cc-account-row" onClick={onClick}>{inner}</button> : <div className="cc-account-row">{inner}</div>;
}


/** Short confirmation line, polite live region. */
export function useToast() {
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => { if (!toast) return; const t = window.setTimeout(() => setToast(null), 2400); return () => window.clearTimeout(t); }, [toast]);
  const node = toast ? <div className="cc-account-toast" role="status">{toast}</div> : null;
  return [node, setToast] as const;
}

/* ---------------- Profil ---------------- */

type Field = "firstName" | "lastName" | "email" | "phone" | "birthDate" | "heightCm";
const FIELDS: Record<Field, { label: string; type: string; inputMode?: "email" | "tel" | "numeric"; autoComplete?: string; check?: (v: string) => string | null }> = {
  firstName: { label: "Prénom", type: "text", autoComplete: "given-name", check: v => v.trim() ? null : "Indique ton prénom." },
  lastName: { label: "Nom", type: "text", autoComplete: "family-name", check: v => v.trim() ? null : "Indique ton nom." },
  email: { label: "E-mail", type: "email", inputMode: "email", autoComplete: "email", check: v => /^\S+@\S+\.\S+$/.test(v.trim()) ? null : "Indique une adresse e-mail valide." },
  phone: { label: "Téléphone", type: "tel", inputMode: "tel", autoComplete: "tel", check: v => v.replace(/\D/g, "").length >= 10 ? null : "Indique un numéro à 10 chiffres." },
  birthDate: { label: "Date de naissance", type: "date", autoComplete: "bday", check: v => v ? null : "Choisis une date." },
  heightCm: { label: "Taille (cm)", type: "number", inputMode: "numeric", check: v => { const n = Number(v); return n >= 100 && n <= 230 ? null : "Indique une taille entre 100 et 230 cm."; } },
};
const longDate = (iso: string) => iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "À compléter";
const shown = (a: Account, f: Field) => f === "birthDate" ? longDate(a.birthDate) : f === "heightCm" ? `${a.heightCm} cm` : String(a[f]);

function EditSheet({ field, onClose, onSaved }: { field: Field; onClose: () => void; onSaved: () => void }) {
  const a = useAccount();
  const cfg = FIELDS[field];
  const [value, setValue] = useState(String(a[field]));
  const [error, setError] = useState<string | null>(null);
  // The existing error is tied to the field (aria-describedby), not only announced (role="alert").
  const errorId = useId();
  const save = () => {
    const err = cfg.check?.(value) ?? null; setError(err); if (err) return;
    account.set(field === "heightCm" ? { heightCm: Math.round(Number(value)) } : { [field]: value.trim() } as Partial<Account>);
    onSaved();
  };
  return <AccountSheet title={cfg.label} onClose={onClose}>
    <form className="cc-account-form" onSubmit={e => { e.preventDefault(); save(); }} noValidate>
      <label className="cc-account-field"><span>{cfg.label}</span>
        <input type={cfg.type} inputMode={cfg.inputMode} autoComplete={cfg.autoComplete} value={value} autoFocus aria-invalid={!!error || undefined} aria-describedby={error ? errorId : undefined}
          min={field === "heightCm" ? 100 : undefined} max={field === "heightCm" ? 230 : field === "birthDate" ? "2012-12-31" : undefined}
          onChange={e => { setValue(e.target.value); setError(null); }} />
      </label>
      {error && <p id={errorId} className="cc-account-error" role="alert">{error}</p>}
      <Button variant="primary" size="lg" type="submit" className="cc-cta">Enregistrer</Button>
    </form>
  </AccountSheet>;
}

export function Profil({ go }: PageProps) {
  const a = useAccount();
  const h = useHealth();
  const [edit, setEdit] = useState<Field | null>(null);
  const [toast, setToast] = useToast();
  const file = useRef<HTMLInputElement>(null);

  return <div className="cc-screen cc-account">
    <StatusBar />
    <div className="cc-scroll">
      <AccountHeader title="Mon profil" go={go} />

      {/* 1 · Identité + Mes informations */}
      <section className="cc-card cc-account-id" aria-label="Identité">
        <Avatar name={fullName(a)} src={a.photo} size="xl" className="cc-account-avatar" />
        <div className="cc-account-id-text">
          <p className="cc-account-name">{fullName(a)}</p>
          <p className="cc-account-since">Membre depuis {a.memberSince}</p>
          <button type="button" className="cc-account-photo" onClick={() => file.current?.click()}><Camera size={14} strokeWidth={2} /> Changer la photo</button>
          <input ref={file} type="file" accept="image/*" hidden onChange={e => { const f = e.target.files?.[0]; if (f) { account.setPhoto(f); setToast("Photo mise à jour · aperçu local, rien n'est envoyé"); } e.target.value = ""; }} />
        </div>
      </section>

      <AccountGroup title="Mes informations" id="pr-infos">
        {(["firstName", "lastName", "email", "phone"] as const).map(f => <AccountRow key={f} label={FIELDS[f].label} value={shown(a, f)} onClick={() => setEdit(f)} />)}
      </AccountGroup>

      {/* 2 · Mes repères */}
      <AccountGroup title="Mes repères" id="pr-reperes"
        note={<>Poids, mensurations et photos de progression sont dans <button type="button" className="cc-account-inline" onClick={() => go("suivi")}>Mon suivi</button>.</>}>
        {(["birthDate", "heightCm"] as const).map(f => <AccountRow key={f} label={f === "heightCm" ? "Taille" : FIELDS[f].label} value={shown(a, f)} onClick={() => setEdit(f)} />)}
        {/* Questionnaire santé (14/10): its review status, only under the explicit configuration. */}
        {isConfigured(h) && <AccountRow label="Questionnaire santé" value={STATUS_LABEL[h.review.status]} onClick={() => { window.location.hash = "#/sante"; }} />}
      </AccountGroup>

      <p className="cc-home-demo cc-account-demo">Prototype : informations fictives, modifiées sur cet appareil le temps de la visite, rien n'est envoyé.</p>
    </div>
    {edit && <EditSheet field={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); setToast("Enregistré"); }} />}
    {toast}
  </div>;
}
